import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useLang, LangToggle } from '../i18n';

const GOLD = '#F5C518';

function isSafeTaskUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export default function Tasks() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    API.get('/api/tasks/list')
      .then(d => {
        if (!alive) return;
        setTasks(Array.isArray(d.list) ? d.list : []);
      })
      .catch(e => {
        if (!alive) return;
        setError(`${e || '加载失败'}`);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  function openTask(task) {
    if (!isSafeTaskUrl(task?.url)) return;

    const link = document.createElement('a');
    link.href = task.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();

    if (task.id) {
      API.post(`/api/tasks/${task.id}/click`).catch(() => {});
    }
  }

  const S = `
    *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;}
    .tasks-root{min-height:100vh;min-height:100dvh;
      background:linear-gradient(180deg,#0B1E4A 0%,#0D2B6B 52%,#091840 100%);
      background-attachment:fixed;padding:0 0 40px;overflow-x:hidden;}
    .tasks-glow{position:fixed;top:0;left:0;right:0;height:360px;pointer-events:none;
      background:radial-gradient(ellipse at top,rgba(46,127,232,0.2) 0%,transparent 60%);
      z-index:0;}
    .tasks-inner{position:relative;z-index:1;max-width:480px;margin:0 auto;}
    .tasks-header{display:flex;align-items:center;gap:14px;padding:52px 20px 22px;}
    .tasks-back{background:rgba(46,127,232,0.15);border:1px solid rgba(46,127,232,0.3);
      color:#8BA8D4;font-size:18px;cursor:pointer;line-height:1;padding:8px 12px;
      border-radius:10px;}
    .tasks-title{font-size:20px;font-weight:800;color:#fff;}
    .tasks-list{padding:0 20px;}
    .task-row{width:100%;border:none;margin:0 0 12px;padding:14px 14px;
      background:#0F2554;border-radius:16px;display:flex;align-items:center;gap:12px;
      text-align:left;cursor:pointer;box-shadow:inset 0 1px 0 rgba(255,255,255,0.07);
      transition:background 0.15s,transform 0.15s;}
    .task-row:hover{background:#152D6A;}
    .task-row:active{transform:translateY(2px);}
    .task-icon-wrap{width:46px;height:46px;border-radius:12px;background:#fff;
      display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;}
    .task-icon{width:34px;height:34px;object-fit:contain;display:block;}
    .task-copy{flex:1;min-width:0;font-size:14px;line-height:1.45;color:#fff;font-weight:650;
      overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;}
    .task-reward{min-width:86px;max-width:118px;text-align:right;font-family:'Share Tech Mono',monospace;
      color:${GOLD};font-size:14px;font-weight:900;line-height:1.2;word-break:break-word;}
    .tasks-state{margin:0 20px;padding:32px 18px;border-radius:16px;background:#0F2554;
      color:#8BA8D4;text-align:center;font-size:14px;box-shadow:inset 0 1px 0 rgba(255,255,255,0.06);}
    .tasks-error{color:#E8453C;}
    @media (max-width:360px){
      .task-row{padding:12px;gap:10px;}
      .task-icon-wrap{width:42px;height:42px;border-radius:11px;}
      .task-icon{width:31px;height:31px;}
      .task-copy{font-size:13px;}
      .task-reward{min-width:76px;max-width:96px;font-size:13px;}
    }
  `;

  return (
    <div className="tasks-root">
      <style>{S}</style>
      <div className="tasks-glow" />
      <div className="tasks-inner">
        <div className="tasks-header">
          <button className="tasks-back" onClick={() => nav('/lobby')}>&#8592;</button>
          <div className="tasks-title">{t('taskTitle')}</div>
          <LangToggle />
        </div>

        {loading ? (
          <div className="tasks-state" style={{fontFamily:'Share Tech Mono'}}>{t('loading')}</div>
        ) : error ? (
          <div className="tasks-state tasks-error">{error}</div>
        ) : tasks.length === 0 ? (
          <div className="tasks-state">{lang==='en'?'No tasks available':'暂无任务'}</div>
        ) : (
          <div className="tasks-list">
            {tasks.map(task => (
              <button
                key={task.id}
                className="task-row"
                type="button"
                onClick={() => openTask(task)}
                aria-label={task.description}
              >
                <span className="task-icon-wrap">
                  <img
                    className="task-icon"
                    src={task.iconUrl || '/ansem-logo.webp'}
                    alt=""
                    onError={e => {
                      if (e.currentTarget.dataset.fallback) return;
                      e.currentTarget.dataset.fallback = '1';
                      e.currentTarget.src = '/ansem-logo.webp';
                    }}
                  />
                </span>
                <span className="task-copy">{task.description}</span>
                <span className="task-reward">{task.reward}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
